import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { Observable, scheduled, asyncScheduler } from 'rxjs';

import { ToastService } from '../../../../../shared/toast/toast.service';
import { ErrorService } from '../../../../../shared/services/error-service';
import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';
import { EntityConstants } from '../../../../../shared/constants/entity.constants';

import { AnnexureBatchResult, AnnexureService, WorkbookService, ImportColumnMappingService, ImportColumnService, ColumnMapping, CreateColumnMappingInput } from '../../../../../../service/proxy';

@Component({
    selector: 'annexure-column-selection',
    templateUrl: './annexure-column-selection.component.html',
    styleUrls: ['./annexure-column-selection.component.scss'],

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        AnnexureService,
        ImportColumnService,
        ImportColumnMappingService,
        WorkbookService,
    ]
})
export class AnnexureColumnSelectionComponent implements OnInit, WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;
    public isWorksheetSelected: boolean = false;
    public headerIndex: number = 0;
    public batch: AnnexureBatchResult = new AnnexureBatchResult();

    public worksheetName: any[] = [];
    public columnMappings: any[] = [];
    public databaseColumns: any[] = [];
    public validationMessage: string = '';
    
    private originalMappings: ColumnMapping[] = [];

    constructor(
        private route: ActivatedRoute,
        private annexureService: AnnexureService,
        private workbookService: WorkbookService,
        private importColumnService: ImportColumnService,
        private toastService: ToastService,
        private errorService: ErrorService
    ) {}

    ngOnInit(): void {

    }

    async onActivate(): Promise<void> {
        this.route.paramMap.subscribe(async (params) => {
            this.batchId = Number(params.get('id'));

            if (this.batchId > 0) {
                try {
                    const batch = await this.getBatchById();

                    await this.getHeaderIndex(this.batchId);
                    await this.loadDatabaseColumnHeaders();
                    await this.loadWorkbookColumnHeaders(batch);
                    await this.getImportColumnMapping();
                }
                catch (error) {
                    this.toastService.show('An error occurred while activating the component. Please try again.', 'Error');
                    this.errorService.handleError;
                }
            }
        });
    }

    private getBatchById(): Promise<AnnexureBatchResult> {
        return new Promise((resolve, reject) => {
            this.annexureService.getAnnexureBatch(this.batchId).subscribe({
                next: (result) => {
                    this.batch = result;

                    resolve(result);
                },
                error: (error) => {
                    this.toastService.show('Failed to load batch data. Please try again.', 'Error');
                    this.errorService.handleError;

                    reject(error);
                }
            });
        });
    }

    private getHeaderIndex(batchId: number): Promise<number> {
        return new Promise((resolve, reject) => {
            this.annexureService.getAnnexureImportRangeMapping(batchId).subscribe({
                next: (result) => {
                    this.headerIndex = result?.headerIndex ?? 1;

                    resolve(this.headerIndex);
                },
                error: (error) => {
                    this.toastService.show('Failed to retrieve header row info.', 'Error');
                    this.errorService.handleError(error);

                    reject(error);
                }
            });
        });
    }

    private loadDatabaseColumnHeaders(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.importColumnService.getImportColumnsByEntityName(EntityConstants.Annexure).subscribe({
                next: (columns) => {
                    this.databaseColumns = columns;

                    resolve();
                },
                error: (error) => {
                    this.toastService.show('Failed to load database columns. Please try again.', 'Error');
                    this.errorService.handleError;

                    reject(error);
                }
            });
        });
    }

    private loadWorkbookColumnHeaders(batch: AnnexureBatchResult): Promise<void> {
        return new Promise((resolve, reject) => {
            const filePath = `${batch.filePath}\\${batch.storedName}`;
            const sheetIndex = 0;

            this.workbookService.getColumnsBySheetIndex(filePath, sheetIndex, this.headerIndex)
                .subscribe({
                    next: (result) => {
                        this.columnMappings = result.headers.map((header: any, index: number) => ({
                            columnIndex: index,
                            sourceColumnDescription: header.label || `Column ${index + 1}`,
                            destinationColumn: '',
                            formatString: '',
                            regex: '',
                            showAdvanced: false
                        }));

                        resolve();
                    },
                    error: (error) => {
                        this.toastService.show('Failed to load headers from file. Please try again.', 'Error');
                        this.errorService.handleError(error);

                        reject(error);
                    }
                });
        });
    }

    private getImportColumnMapping(): Promise<void> {
        return new Promise((resolve, reject) => {
            this.annexureService.getAnnexureImportColumnMapping(this.batchId).subscribe({
                next: (result) => {
                    if (!result || !result.mappings || result.mappings.length === 0) {
                        this.mapColumnSelectionsAutomatically();
                        this.originalMappings = [];
                    }
                    else {
                        this.originalMappings = JSON.parse(JSON.stringify(result.mappings));
                        this.mapColumnSelectionsFromDatabase(result.mappings);
                    }

                    resolve();
                },
                error: (error) => {
                    this.toastService.show('Failed to load database columns. Please try again.', 'Error');
                    this.errorService.handleError;

                    reject(error);
                }
            });
        });
    }

    private mapColumnSelectionsAutomatically(): void {
        this.columnMappings.forEach(m => {
            const matchingDatabaseColumn = this.databaseColumns.find(dbCol =>
                dbCol.description.replace(' ', '').toLowerCase().includes(m['sourceColumnDescription'].replace(' ', '').toLowerCase())
            );

            if (matchingDatabaseColumn) {
                m.destinationColumn = matchingDatabaseColumn.name;
            }
        });
    }

    private mapColumnSelectionsFromDatabase(items: ColumnMapping[]): void {
        items.forEach((item: any) => {
            const row = this.columnMappings.find((m: any) => m.columnIndex === item.columnIndex);

            if (row) {
                row.destinationColumn = item.mappedPropertyName ?? row.destinationColumn;
                row.formatString = item.formatString ?? row.formatString;
                row.regex = item.regex ?? row.regex;
            }
        });
    }

    isValid(): boolean {
        // Don't validate until data is loaded
        if (!this.databaseColumns?.length || !this.columnMappings?.length) {
            return true; // Let it proceed if data isn't loaded yet
        }

        // Check for duplicate mappings
        const mappedFields = this.columnMappings
            .map(m => m.destinationColumn)
            .filter(Boolean);
        
        const duplicates = mappedFields.filter((field, index) => 
            mappedFields.indexOf(field) !== index
        );
        
        if (duplicates.length > 0) {
            const uniqueDuplicates = [...new Set(duplicates)];
            const fieldNames = uniqueDuplicates
                .map(fieldName => this.databaseColumns.find(col => col.name === fieldName)?.description || fieldName)
                .join(', ');
            this.validationMessage = `The following fields are mapped multiple times: ${fieldNames}. Each field can only be mapped once.`;
            return false;
        }

        // Check if all required database columns have been mapped
        const requiredColumns = this.databaseColumns.filter(col => col.required);
        const mappedColumns = this.columnMappings
            .filter(m => m.destinationColumn)
            .map(m => m.destinationColumn);
        
        const unmappedRequired = requiredColumns.filter(
            req => !mappedColumns.includes(req.name)
        );
        
        if (unmappedRequired.length > 0) {
            const fieldNames = unmappedRequired.map(col => col.description).join(', ');
            this.validationMessage = `Please map all required fields before proceeding. Missing: ${fieldNames}`;
            return false;
        }
        
        this.validationMessage = '';
        return true;
    }

    onMappingChange(): void {
        // Re-validate whenever a dropdown changes
        this.isValid();
    }

    private haveMappingsChanged(): boolean {
        if (this.originalMappings.length === 0) {
            return true;
        }

        return this.columnMappings.some(current => {
            const original = this.originalMappings.find(
                orig => orig.columnIndex === current.columnIndex
            );

            if (!original) {
                return true;
            }

            return (
                original.mappedPropertyName !== current.destinationColumn ||
                (original.formatString ?? '') !== (current.formatString ?? '') ||
                (original.regex ?? '') !== (current.regex ?? '')
            );
        });
    }

    onNext(): Observable<boolean> {
        if (!this.batchId || this.columnMappings.length === 0) {
            this.toastService.show('No column mappings to save.');
            return scheduled([false], asyncScheduler);
        }

        const payload = new CreateColumnMappingInput();

        payload.mappings = this.columnMappings.map(({ columnIndex, destinationColumn, formatString, regex }) => {
            const mapped = new ColumnMapping();

            mapped.columnIndex = columnIndex;
            mapped.mappedPropertyName = destinationColumn;
            mapped.formatString = formatString || undefined;
            mapped.regex = regex || undefined;

            return mapped;
        });

        return new Observable<boolean>(observer => {
            this.annexureService.createAnnexureColumnMapping(this.batchId, payload).subscribe({
                next: () => {
                    this.toastService.show('Mappings saved successfully!', 'Success');

                    const mappingsChangedKey = `annexure_mappings_changed_${this.batchId}`;

                    // Only write the flag if a prior step (range mapping) hasn't already set it to true.
                    // If we overwrite 'true' with 'false' here, the import step won't re-run
                    // even though the row range changed.
                    const alreadyFlagged = sessionStorage.getItem(mappingsChangedKey) === 'true';
                    if (!alreadyFlagged) {
                        const hasChanged = this.haveMappingsChanged();
                        sessionStorage.setItem(mappingsChangedKey, hasChanged.toString());
                    }

                    observer.next(true);
                    observer.complete();
                },
                error: () => {
                    this.toastService.show('Failed to save column mappings. Please try again.', 'error');

                    observer.next(false);
                    observer.complete();
                }
            });
        });
    }

    onPrevious(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }

    toggleAdvanced(mapping: any, event: Event): void {
        event.preventDefault();
        mapping.showAdvanced = !mapping.showAdvanced;
    }
}
